import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { Button, Container } from 'reactstrap';
import { APIgetPage } from '../../../../api/Pages';

import {
  getCollectionDestination,
  getFileDestination,
  normalizePageVisibilityMode,
} from '../../../../helpers/PageSections';
import { isValidUrl } from '../../../../helpers/Validators';
import { normalizeHydratedPageFile } from '../Common/pageFileHydration';
import { getPageFileByUuid } from '../Common/pageFiles';
import {
  usePageId,
  usePageIsEditing,
  usePageBasePath,
  usePageRouteId,
  usePageVisibilityMode,
} from '../../../../helpers/PageIdContext';
import formatIntl from '../Common/formatIntl';
import {
  collectionShape,
  fileShape,
  pageShape,
} from './ButtonFields';

const pageSlugByIdCache = new Map();

const toPagePath = (pageRouteToken, pageBasePath = 'pages') => {
  if (pageRouteToken == null || pageRouteToken === '') return '';

  const routeToken = String(pageRouteToken);
  if (isValidUrl(routeToken)) return routeToken;

  if (routeToken.startsWith('/')) {
    return routeToken;
  }

  return `/${pageBasePath}/${routeToken}`;
};

const resolveDestination = ({
  targetType,
  targetCollection,
  targetFile,
  targetPage,
  url,
  pageVisibilityMode,
  pageBasePath,
  pageRouteId,
  pageId,
  backUrl,
}) => {
  if (targetType === 'collection' && targetCollection?.uuid) {
    return getCollectionDestination({
      collection: targetCollection,
      pageVisibilityMode,
      pageBasePath,
      pageRouteId,
    });
  }

  if (targetType === 'file' && targetFile?.uuid) {
    return getFileDestination({
      file: targetFile,
      pageVisibilityMode,
      pageBasePath,
      pageRouteId,
      pageId,
      backUrl,
    });
  }

  if (targetType === 'page' && (targetPage?.id || targetPage?.url)) {
    const pageRouteToken = targetPage?.slugResolved
      ? (targetPage?.url || targetPage?.id)
      : (targetPage?.id || targetPage?.url);

    return toPagePath(pageRouteToken, pageBasePath);
  }

  if (targetType === 'url' && url) {
    return url;
  }

  return '';
};

const isExternalDestination = (targetType, destination) => (
  targetType === 'url' && isValidUrl(destination)
);

const PuckButtonRender = ({
  text,
  size,
  color,
  align,
  targetType,
  targetCollection,
  targetFile,
  targetPage,
  url,
  page_visibility_mode: pageVisibilityMode,
}) => {
  const location = useLocation();
  const pageId = usePageId();
  const isEditing = usePageIsEditing();
  const pageBasePath = usePageBasePath();
  const pageRouteId = usePageRouteId();
  const contextVisibilityMode = usePageVisibilityMode();
  const effectivePageVisibilityMode = normalizePageVisibilityMode(
    pageVisibilityMode || contextVisibilityMode,
  );
  const backUrl = `${location.pathname}${location.search || ''}`;
  const [resolvedTargetFile, setResolvedTargetFile] = useState(targetFile || null);
  const [resolvedTargetPage, setResolvedTargetPage] = useState(targetPage || null);

  useEffect(() => {
    let mounted = true;

    if (targetType !== 'file' || !targetFile?.uuid || !pageId) {
      setResolvedTargetFile(targetFile || null);
      return () => {
        mounted = false;
      };
    }

    getPageFileByUuid({
      pageId,
      fileUuid: targetFile.uuid,
      fallbackFile: targetFile,
    })
      .then((resolved) => {
        if (!mounted) {
          return;
        }

        setResolvedTargetFile(normalizeHydratedPageFile(targetFile, resolved || targetFile));
      })
      .catch(() => {
        if (!mounted) {
          return;
        }

        setResolvedTargetFile(targetFile || null);
      });

    return () => {
      mounted = false;
    };
  }, [pageId, targetType, targetFile]);

  useEffect(() => {
    let mounted = true;

    if (targetType !== 'page' || !targetPage) {
      setResolvedTargetPage(targetPage || null);
      return () => {
        mounted = false;
      };
    }

    const targetPageId = targetPage.id;
    if (!targetPageId) {
      setResolvedTargetPage(targetPage);
      return () => {
        mounted = false;
      };
    }

    const cacheKey = String(targetPageId);
    const cachedSlug = pageSlugByIdCache.get(cacheKey);

    setResolvedTargetPage({
      ...targetPage,
      url: cachedSlug || '',
      slugResolved: false,
    });

    APIgetPage(targetPageId, isEditing ? { edit_mode: true } : {})
      .then((response) => {
        if (!mounted) {
          return;
        }

        const currentSlug = response?.data?.url;
        if (currentSlug) {
          pageSlugByIdCache.set(cacheKey, currentSlug);
        }

        setResolvedTargetPage({
          ...targetPage,
          url: currentSlug || targetPage.url,
          slugResolved: true,
        });
      })
      .catch(() => {
        if (!mounted) {
          return;
        }

        setResolvedTargetPage({
          ...targetPage,
          slugResolved: false,
        });
      });

    return () => {
      mounted = false;
    };
  }, [targetType, targetPage, isEditing]);

  const destination = resolveDestination({
    targetType,
    targetCollection,
    targetFile: targetType === 'file' ? resolvedTargetFile : targetFile,
    targetPage: targetType === 'page' ? resolvedTargetPage : targetPage,
    url,
    pageVisibilityMode: effectivePageVisibilityMode,
    pageBasePath,
    pageRouteId,
    pageId,
    backUrl,
  });
  const buttonSize = size === 'md' ? undefined : size;
  const label = text || formatIntl('Button');
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align] || 'text-left';

  if (!destination) {
    return (
      <div className={`custom-page-section ${alignClass} puck-button`}>
        <Container>
          <Button color={color || 'primary'} size={buttonSize} disabled>
            {label}
          </Button>
        </Container>
      </div>
    );
  }

  const isExternal = isExternalDestination(targetType, destination);

  return (
    <div className={`custom-page-section ${alignClass} puck-button`}>
      <Container>
        {isExternal ? (
          <Button
            tag="a"
            href={destination}
            color={color || 'primary'}
            size={buttonSize}
            target="_blank"
          >
            {label}
          </Button>
        ) : (
          <Button
            tag={Link}
            to={destination}
            color={color || 'primary'}
            size={buttonSize}
          >
            {label}
          </Button>
        )}
      </Container>
    </div>
  );
};

PuckButtonRender.propTypes = {
  text: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  color: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  targetType: PropTypes.oneOf(['collection', 'file', 'url', 'page']),
  targetCollection: collectionShape,
  targetFile: fileShape,
  targetPage: pageShape,
  url: PropTypes.string,
  page_visibility_mode: PropTypes.string,
};

PuckButtonRender.defaultProps = {
  text: '',
  size: 'lg',
  color: 'primary',
  align: 'left',
  targetType: 'url',
  targetCollection: null,
  targetFile: null,
  targetPage: null,
  url: '',
  page_visibility_mode: null,
};

export default PuckButtonRender;
